import { downloadProductionDailyReportAttachment } from '../../../api/productionDailyReport';
import {
  ProductionDailyMaterial,
  ProductionDailyReport,
  ProductionDailyRun,
} from '../../../types/productionDailyReport';
import ApprovalStampBox from '../ApprovalStampBox';
import { PreviewAttachmentList } from './previewShared';
import '../../quality/QualityShell.css';
import '../../quality/EducationAnnualPlanTab.css';
import '../../product/InspectionFormSheet.css';
import '../../product/NonconformanceReportPanel.css';
import '../../product/ProductionDailyReportPanel.css';

interface Props {
  detail: ProductionDailyReport;
}

function uid(prefix = 'r') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function qtyText(value: unknown): string {
  if (value == null || value === '') return '';
  return String(value);
}

function parseId(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function isLegacyFlatItem(raw: Record<string, unknown>): boolean {
  const looksLikeProduct =
    'productId' in raw || 'goodQty' in raw || 'productName' in raw;
  const hasRunFields = Array.isArray(raw.materials) || 'workStart' in raw;
  return looksLikeProduct && !hasRunFields;
}

function parseMaterial(raw: Record<string, unknown>): ProductionDailyMaterial {
  return {
    id: String(raw.id || uid('m')),
    acceptanceInspectionId: parseId(raw.acceptanceInspectionId),
    inventoryId: parseId(raw.inventoryId),
    itemName: String(raw.itemName || ''),
    specText: String(raw.specText || ''),
    lotNo: String(raw.lotNo || ''),
    qty: qtyText(raw.qty),
    remark: String(raw.remark || ''),
  };
}

function parseRun(raw: Record<string, unknown>): ProductionDailyRun {
  const materialsRaw = Array.isArray(raw.materials) ? raw.materials : [];
  return {
    id: String(raw.id || uid('r')),
    productId: parseId(raw.productId),
    productCode: String(raw.productCode || ''),
    productName: String(raw.productName || ''),
    spec: String(raw.spec || ''),
    unit: String(raw.unit || ''),
    workStart: String(raw.workStart || ''),
    workEnd: String(raw.workEnd || ''),
    planQty: qtyText(raw.planQty),
    goodQty: qtyText(raw.goodQty),
    defectQty: qtyText(raw.defectQty),
    remark: String(raw.remark || ''),
    materials: materialsRaw.map((m) => parseMaterial((m || {}) as Record<string, unknown>)),
  };
}

function parseRuns(json?: string | null): ProductionDailyRun[] {
  if (!json?.trim()) return [];
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr) || !arr.length) return [];
    return arr.map((raw: Record<string, unknown>) => {
      if (isLegacyFlatItem(raw)) {
        const legacyLot = String(raw.lotNo || '').trim();
        const remark = String(raw.remark || '');
        return parseRun({
          ...raw,
          materials: [],
          workStart: '',
          workEnd: '',
          remark: legacyLot
            ? [remark, `구 LOT: ${legacyLot}`].filter(Boolean).join(' / ')
            : remark,
        });
      }
      return parseRun(raw);
    });
  } catch {
    return [];
  }
}

function shiftLabel(shift?: string | null) {
  switch (shift) {
    case 'DAY':
      return '주간';
    case 'NIGHT':
      return '야간';
    case 'OTHER':
      return '기타';
    default:
      return shift || '-';
  }
}

function statusLabel(status?: string | null, approvalInProgress?: boolean | null) {
  if (approvalInProgress) return '결재 요청 중';
  if (status === 'APPROVED') return '승인';
  if (status === 'CONFIRMED') return '확정';
  return '작성중';
}

function ProductionDailyPreviewView({ detail }: Props) {
  const detailRuns = parseRuns(detail.itemsJson).filter(
    (run) => run.productId != null || run.productName.trim()
  );

  return (
    <div className="ncr-detail">
      <div
        className="ifs-sheet"
        style={{ marginBottom: 14, padding: '12px 14px', background: '#fff' }}
      >
        <div className="ifs-sheet__top" style={{ marginBottom: 0 }}>
          <div>
            <h4 className="ifs-sheet__title" style={{ margin: 0, fontSize: 15 }}>
              생산일보 {detail.reportNo || ''}
            </h4>
            <p className="ifs-sheet__subtitle" style={{ margin: '4px 0 0' }}>
              {detail.reportDate || '-'} · {statusLabel(detail.status, detail.approvalInProgress)}
            </p>
          </div>
          <ApprovalStampBox
            authorName={detail.authorName}
            reviewerName={detail.reviewerName}
            approverName={detail.approverName}
          />
        </div>
      </div>
      <section className="ncr-detail__card">
        <h3>기본 정보</h3>
        <dl>
          <dt>번호</dt>
          <dd>{detail.reportNo || '-'}</dd>
          <dt>일자</dt>
          <dd>{detail.reportDate || '-'}</dd>
          <dt>교대</dt>
          <dd>{shiftLabel(detail.shiftType)}</dd>
          <dt>상태</dt>
          <dd>
            <span className={`quality-badge is-${(detail.status || 'draft').toLowerCase()}`}>
              {statusLabel(detail.status, detail.approvalInProgress)}
            </span>
          </dd>
          <dt>라인/작업장</dt>
          <dd>{detail.workCenter || '-'}</dd>
          <dt>책임자</dt>
          <dd>{detail.supervisorName || '-'}</dd>
          <dt>작성자</dt>
          <dd>{detail.authorName || '-'}</dd>
        </dl>
      </section>

      <section className="ncr-detail__card">
        <h3>비가동</h3>
        <dl>
          <dt>비가동(분)</dt>
          <dd>{detail.downtimeMinutes ?? '-'}</dd>
          <dt>비가동 사유</dt>
          <dd>{detail.downtimeReason || '-'}</dd>
        </dl>
      </section>

      <section className="ncr-detail__card is-wide">
        <h3>인원·설비</h3>
        <dl>
          <dt>작업인원</dt>
          <dd>{detail.workersText || '-'}</dd>
          <dt>설비</dt>
          <dd>{detail.equipmentText || '-'}</dd>
        </dl>
      </section>

      <section className="ncr-detail__card is-wide">
        <h3>생산 구간</h3>
        {detailRuns.length === 0 ? (
          <p className="quality-form__hint">등록된 생산 구간이 없습니다.</p>
        ) : (
          detailRuns.map((run) => {
            const timeRange =
              run.workStart || run.workEnd
                ? `${run.workStart || '?'} – ${run.workEnd || '?'}`
                : '시간 미입력';
            const product = run.productCode
              ? `${run.productCode} · ${run.productName || '-'}`
              : run.productName || '-';
            return (
              <div key={run.id} className="pdr-detail-run">
                <p className="pdr-detail-run__title">
                  {timeRange} · {product}
                </p>
                <p className="pdr-detail-run__qtys">
                  계획 {run.planQty || '-'} · 양품 {run.goodQty || '-'} · 불량{' '}
                  {run.defectQty || '-'}
                  {run.unit ? ` (${run.unit})` : ''}
                  {run.spec ? ` · ${run.spec}` : ''}
                </p>
                {run.materials.length > 0 ? (
                  <ul className="pdr-detail-run__materials">
                    {run.materials.map((m) => (
                      <li key={m.id}>
                        {[m.lotNo ? `Lot ${m.lotNo}` : null, m.itemName || null]
                          .filter(Boolean)
                          .join(' · ') || '투입'}
                        {m.qty ? ` · ${m.qty}` : ''}
                        {m.remark ? ` (${m.remark})` : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="quality-form__hint">투입 자재 없음</p>
                )}
              </div>
            );
          })
        )}
      </section>

      <section className="ncr-detail__card is-wide">
        <h3>내용</h3>
        <dl>
          <dt>작업 요약</dt>
          <dd>{detail.summaryText || '-'}</dd>
          <dt>이슈/특이사항</dt>
          <dd>{detail.issueText || '-'}</dd>
          <dt>비고</dt>
          <dd>{detail.remark || '-'}</dd>
        </dl>
      </section>

      <section className="ncr-detail__card is-wide">
        <h3>첨부파일</h3>
        <PreviewAttachmentList
          files={detail.attachments}
          onDownload={(file) =>
            void downloadProductionDailyReportAttachment(
              detail.reportId,
              file.attachmentId,
              file.fileName
            ).catch(() => undefined)
          }
        />
      </section>
    </div>
  );
}

export default ProductionDailyPreviewView;
